// ── BCH Rooms — discovery ─────────────────────────────────────────
//
// Rooms are NIP-53 kind 30312 events; liveness comes from kind 10312
// presence rather than from the room event itself. That split matters:
// a room event is addressable and persists forever once published, so
// "does this room exist" and "is anyone in it right now" are genuinely
// different questions. Presence answers the second one honestly, and
// gives an accurate head count for free.

import { useMemo } from 'react';
import { useNostrQuery } from './useNostrQuery';
import {
  ROOM_KIND, ROOM_PRESENCE_KIND, PRESENCE_TIMEOUT_MS,
  dedupeRooms, presenceCounts, type Room,
} from '../lib/rooms';

export interface LiveRoom extends Room {
  /** Participants with fresh presence. */
  participants: number;
}

export interface UseRoomsResult {
  rooms: LiveRoom[];
  liveRooms: LiveRoom[];
  loading: boolean;
  refetch: () => void;
}

export function useRooms(): UseRoomsResult {
  // Rooms are public UGC — anyone may host one, so there is no author
  // filter. Look back a week: an addressable room event is only
  // re-published when its metadata changes, so a busy long-lived room
  // may have an old 30312 while still being active via presence.
  const roomsSince = useMemo(
    () => Math.floor(Date.now() / 1000) - 7 * 86400,
    [],
  );
  const presenceSince = useMemo(
    () => Math.floor(Date.now() / 1000) - Math.ceil(PRESENCE_TIMEOUT_MS / 1000) * 2,
    [],
  );

  const { events: roomEvents, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [ROOM_KIND], since: roomsSince, limit: 200 }],
    allRelays: true,
  });

  const { events: presenceEvents, refetch: refetchPresence } = useNostrQuery({
    filters: [{ kinds: [ROOM_PRESENCE_KIND], since: presenceSince, limit: 500 }],
    allRelays: true,
  });

  const rooms = useMemo(() => {
    const counts = presenceCounts(presenceEvents);
    return dedupeRooms(roomEvents)
      // Closed rooms are explicitly not in operation (NIP-53).
      .filter(r => r.status !== 'closed')
      .map(r => ({ ...r, participants: counts.get(r.coordinate) || 0 }))
      .sort((a, b) => {
        if (b.participants !== a.participants) return b.participants - a.participants;
        return b.createdAt - a.createdAt;
      });
  }, [roomEvents, presenceEvents]);

  const liveRooms = useMemo(
    () => rooms.filter(r => r.participants > 0),
    [rooms],
  );

  const refetchAll = useMemo(
    () => () => { refetch(); refetchPresence(); },
    [refetch, refetchPresence],
  );

  return { rooms, liveRooms, loading, refetch: refetchAll };
}
